<template>
  <lf-modal :title="$t('DEALS.NEW_DEAL')" :close="close">
    <template #content>
      <form
        novalidate
        title="modal-form"
        data-cy="new-deal-form"
        @submit.prevent="submit"
      >
        <div class="space-y-5 p-6">
          <lf-input
            id="business_name"
            disabled
            :placeholder="$t('BUSINESS_PROFILES.NAME')"
            :value="deal.business.business_legal_name"
            name="business_legal_name"
            data-cy="business-name"
          />
          <lf-dropdown-with-api-search
            v-if="!isClient"
            :value="selectedOrganization?.name"
            :placeholder="$t('DEALS.ORGANIZATION')"
            :search-term="$t('DEALS.ORGANIZATION')"
            :get-paginated-options="getApprovedClients"
            name="client_id"
            data-cy="organization-dropdown"
            accessor="name"
            full-width
            show-clear-filter
            @change="changeSelectedOrganization"
          />
          <lf-dropdown
            :value="selectedWorkflowCategory"
            name="workflow"
            :placeholder="$t('DEALS.TABLE_HEADERS.WORKFLOW_CATEGORY')"
            :options="workflowCategoriesDropdownOptions"
            full-width
            data-cy="workflow-dropdown"
            @change="changeSelectedCategory"
          />

          <lf-dropdown
            :value="values.workflow_template_id"
            name="workflow_template_id"
            :placeholder="$t('WORKFLOW.TEMPLATES', 1)"
            :options="getDropdownOptions(workflowTemplates)"
            data-cy="new-deal-select-workflow"
            full-width
            search-enabled
            @change="values.workflow_template_id = $event"
          />

          <template v-if="isLendflowUser">
            <lf-dropdown-with-infinite-query
              :value="values.funding_advisor_id"
              name="funding_advisor_id"
              :placeholder="`${$t('COMMON.LENDFLOW')} ${$t(
                'BUSINESS_PROFILES.FUNDING_ADVISOR'
              )}`"
              :query-options="{
                queryKey: getFundingAdvisorsQueryKey,
                queryFn: getFundingAdvisors,
              }"
              accessor="full_name"
              identifier="id"
              data-cy="onboarding-dropdown"
              full-width
              @change="handleUserChange('funding_advisor_id', $event)"
            />

            <lf-dropdown-with-infinite-query
              :value="values.underwriter_id"
              name="underwriter_id"
              :placeholder="`${$t('COMMON.LENDFLOW')} ${$t(
                'BUSINESS_PROFILES.UNDERWRITER'
              )}`"
              :query-options="{
                queryKey: getUnderwritersQueryKey,
                queryFn: getUnderwriters,
              }"
              accessor="full_name"
              identifier="id"
              data-cy="processor-dropdown"
              full-width
              @change="handleUserChange('underwriter_id', $event)"
            />

            <lf-dropdown-with-infinite-query
              :value="values.analyst_id"
              name="analyst_id"
              :placeholder="`${$t('COMMON.LENDFLOW')} ${$t(
                'BUSINESS_PROFILES.ANALYST'
              )}`"
              :query-options="{
                queryKey: getAnalystsQueryKey,
                queryFn: getAnalysts,
              }"
              accessor="full_name"
              identifier="id"
              data-cy="analyst-dropdown"
              full-width
              @change="handleUserChange('analyst_id', $event)"
            />
          </template>

          <template v-if="isClient">
            <lf-dropdown-with-infinite-query
              :value="values.client_funding_advisor_id"
              name="client_funding_advisor_id"
              :placeholder="`${$t('BUSINESS_PROFILES.FUNDING_ADVISOR')}`"
              :query-options="{
                queryKey: getClientFundingAdvisorsQueryKey,
                queryFn: getClientFundingAdvisors,
              }"
              accessor="full_name"
              identifier="id"
              data-cy="client-onboarding-dropdown"
              full-width
              @change="handleUserChange('client_funding_advisor_id', $event)"
            />

            <lf-dropdown-with-infinite-query
              :value="values.client_underwriter_id"
              name="client_underwriter_id"
              :placeholder="`${$t('BUSINESS_PROFILES.UNDERWRITER')}`"
              :query-options="{
                queryKey: getClientUnderwritersQueryKey,
                queryFn: getClientUnderwriters,
              }"
              accessor="full_name"
              identifier="id"
              data-cy="client-processor-dropdown"
              full-width
              @change="handleUserChange('client_underwriter_id', $event)"
            />

            <lf-dropdown-with-infinite-query
              :value="values.client_analyst_id"
              name="client_analyst_id"
              :placeholder="`${$t('BUSINESS_PROFILES.ANALYST')}`"
              :query-options="{
                queryKey: getClientAnalystsQueryKey,
                queryFn: getClientAnalysts,
              }"
              accessor="full_name"
              identifier="id"
              data-cy="client-analyst-dropdown"
              full-width
              @change="handleUserChange('client_analyst_id', $event)"
            />
          </template>

          <div class="flex justify-end border-t border-gray-200 px-6 pt-5">
            <lf-button
              class="mr-4 border text-gray-500"
              data-cy="cancel-button"
              @click.prevent="close"
            >
              {{ $t("COMMON.CANCEL") }}
            </lf-button>
            <primary-button
              :disabled="isLoading"
              type="submit"
              data-cy="create-button"
            >
              {{ $t("COMMON.CREATE") }}
            </primary-button>
          </div>
        </div>
      </form>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import { useForm } from "vee-validate";

import { Ability, PermissionSubject } from "@/enums/auth";
import { getDropdownOptions } from "@/helpers/common";
import { useAuth, usePermissions } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { useDealsFilters } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import { useUsersForFilters } from "@/hooks/useUsersForFilters";
import { useWorkflowCategoriesInfo } from "@/hooks/workflow";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import { getApprovedClients } from "@/services/modules/clients";

import LfModal from "@/components/ui/Modal.vue";
import LfDropdownWithApiSearch from "@/components/ui/inputs/LfDropdownWithApiSearch.vue";
import LfDropdownWithInfiniteQuery from "@/components/ui/inputs/LfDropdownWithInfiniteQuery.vue";

import type { IApplication } from "@/models/applications";
import type { IClient } from "@/models/clients";
import type { IUser } from "@/models/users";
import type {
  IWorkflow,
  IWorkflowData,
  WorkflowCategories,
  WorkflowTemplatesPayloadParams,
} from "@/models/workflows";
import type { PropType } from "vue";

const props = defineProps({
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },

  deal: {
    type: Object as PropType<IApplication>,
    required: true,
  },
});

const values = reactive({
  id: "",
  workflow_template_id: "",
  funding_advisor_id: "",
  underwriter_id: "",
  analyst_id: "",
  client_funding_advisor_id: "",
  client_underwriter_id: "",
  client_analyst_id: "",
});

const router = useRouter();
const { isClient, isLendflowUser, currentUser } = useAuth();
const { dispatch, commit } = useStore();
const { setActiveFilterId } = useDealsFilters();
const {
  getUnderwriters,
  getUnderwritersQueryKey,
  getClientUnderwriters,
  getClientUnderwritersQueryKey,
  getFundingAdvisors,
  getFundingAdvisorsQueryKey,
  getClientFundingAdvisors,
  getClientFundingAdvisorsQueryKey,
  getAnalysts,
  getAnalystsQueryKey,
  getClientAnalysts,
  getClientAnalystsQueryKey,
} = useUsersForFilters({ ignoreAssignedDeals: true });

const handleUserChange = (
  field: keyof typeof values,
  user: IUser | undefined
) => {
  values[field] = user?.id?.toString() ?? "";
};
const { authClientSettings } = useClients();
const { checkPermissions } = usePermissions();
const { t } = useI18n();
const { showMessage } = useNotification();
const { handleSubmit } = useForm({ initialValues: values });
const { workflowCategoriesDropdownOptions } = useWorkflowCategoriesInfo();

const isLoading = ref(false);
const selectedOrganization = ref<IClient | null>(null);
const selectedWorkflowCategory = ref(props.deal.workflow_category);
const workflowTemplates = ref<IWorkflow[]>([]);

const changeSelectedOrganization = (value: IClient): void => {
  selectedOrganization.value = value;
  values.workflow_template_id = "";
};

const changeSelectedCategory = (category: WorkflowCategories): void => {
  selectedWorkflowCategory.value = category;
  values.workflow_template_id = "";
};

const getTemplatesOptions = async () => {
  const params: WorkflowTemplatesPayloadParams = {
    active: "1",
    global: "1",
  };

  if (selectedWorkflowCategory.value) {
    params.category = selectedWorkflowCategory.value;
  }

  if (!isClient && selectedOrganization.value?.id) {
    params.clients = [selectedOrganization.value.id];
  }

  try {
    const workflowData: IWorkflowData = await dispatch(
      "workflows/getWorkflowTemplates",
      { params }
    );
    workflowTemplates.value = workflowData?.data || [];
  } catch (e) {
    commit("setGlobalMessage", {
      title: t("COMMON.ERROR_OCCURRED"),
      type: "error",
    });
  }
};

const submit = handleSubmit(async (values) => {
  if (!values.workflow_template_id) {
    showMessage(t("DEALS.ERROR_CREATE_NEW_DEAL"), "error");
    return;
  }

  if (isLoading.value) {
    return;
  }

  isLoading.value = true;

  (Object.keys(values) as Array<keyof typeof values>).forEach((key) => {
    const value = values[key];
    if (!value || value === "null") {
      delete values[key];
    }
  });

  delete values["business_legal_name" as keyof typeof values];
  delete values["search" as keyof typeof values];
  delete values["workflow" as keyof typeof values];

  try {
    const newDeal = await dispatch("applications/duplicateDeal", {
      data: {
        ...values,
        client_id: selectedOrganization.value?.id,
      },
      id: props.deal.id,
    });

    const canViewNewDeal = await checkPermissions(
      PermissionSubject.application,
      Ability.view,
      { application: newDeal.id }
    );

    if (canViewNewDeal) {
      router.push({
        name: ROUTE_DEAL_PROGRESS,
        params: { id: newDeal.id },
      });
    }
    setActiveFilterId(-1);
    props.close();
  } finally {
    isLoading.value = false;
  }
});

onMounted(() => {
  if (isClient && !!currentUser?.client_id) {
    selectedOrganization.value = authClientSettings.value || null;
  }

  getTemplatesOptions();
});

watch([selectedWorkflowCategory, selectedOrganization], () => {
  getTemplatesOptions();
});
</script>
